import { CSSProperties, FC, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useLazyQuery, useMutation } from '@apollo/client'

import { useAppDispatch, useAppSelector } from 'hooks/store'
import GoBackHeader from 'components/organisms/GoBackHeader'
import {
  GET_NOTIFICATIONS_BY_CASE,
  GET_ALL_NOTIFICATIONS,
  NotificationListResponse
} from 'share/queries/notifications'
import { MARK_AS_READ, MarkAsReadResponse } from 'share/mutations/notifications'
import { Notification } from 'share/domain/notification'
import {
  GET_CASE_LEVEL_DASHBOARD,
  GetCaseLevelDashboardResponse
} from 'share/queries/cases'
import {
  ActiveCase,
  setActiveCase,
  loadFirstActiveCaseRecordFromResponse
} from 'reducers/activecase/activecaseSlice'
import { routeToQuestionnairePage } from 'share/utils/questionnaireUtils'
import {
  setTabFirst,
  setTabSecond
} from 'reducers/activecourse/activecourseSlice'

import {
  NotificationsListContainer,
  NotificationsListItem,
  NotificationsListItemDate,
  NotificationsListItemDescription,
  NotificationsListItemIcon
} from './styles'
import { loadingSpinnerPortal } from 'components/atoms/LoadingSpinner'
import {
  setTabReview,
  setTabSubmit
} from 'reducers/document/documentProcessingSlice'

const notReadStyle = { '--not-read': 500 } as CSSProperties

function getNotificationDate(dateStr: string): string {
  try {
    const today = new Date()
    const dt = new Date(dateStr)

    if (
      today.getFullYear() === dt.getFullYear() &&
      today.getMonth() === dt.getMonth() &&
      today.getDate() === dt.getDate()
    ) {
      return dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    } else {
      return dt.toLocaleDateString()
    }
  } catch {
    return dateStr
  }
}

const getIconByType = (type: string): string => {
  switch (type.toLowerCase()) {
    case 'documents':
      return 'mdi-file-multiple-outline'
    case 'document':
      return 'mdi-file-multiple-outline'
    case 'course':
      return 'mdi-certificate'
    case 'questionnaire':
      return 'mdi-clipboard-list-outline'
    case 'comment':
      return 'mdi-comment-alert-outline'
    case 'appointment':
      return 'mdi-calendar-outline'
    case 'refund':
      return 'mdi-currency-usd'
    default:
      return 'mdi-information-outline'
  }
}

const truncateFileName = (text: string) => {
  const strs = text.split(' ')
  const docText = strs[1]
  const len = docText.length

  if (len > 20) {
    const start = docText.substring(0, 14)
    const end = docText.substring(len - 5)
    const newDocText = `${start}...${end}`

    strs.splice(1, 1, newDocText)
    const notificationText = strs.join(' ')

    return notificationText
  }

  return text
}

interface NotificationsHistory {
  caseId?: number
  prevPage?: string
}

const NotificationsList: FC = () => {
  const dispatch = useAppDispatch()
  const history = useHistory<NotificationsHistory>()
  const activeCase = useAppSelector((state) => state.activeCase)
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [notification, setNotification] = useState<Notification | null>(null)

  const [getAllNotifications, { loading, data, error }] =
    useLazyQuery<NotificationListResponse>(GET_ALL_NOTIFICATIONS, {
      fetchPolicy: 'network-only'
    })

  const [notificationMarkAsRead, { loading: markReadLoading }] =
    useMutation<MarkAsReadResponse>(MARK_AS_READ)

  const [
    getNotificationsByCase,
    { loading: loadingById, data: dataById, error: errorById }
  ] = useLazyQuery<NotificationListResponse>(GET_NOTIFICATIONS_BY_CASE, {
    fetchPolicy: 'network-only'
  })

  const [
    getCaseDashboard,
    { loading: loadingCaseById, data: dataCaseById, error: errorCaseById }
  ] = useLazyQuery<GetCaseLevelDashboardResponse>(GET_CASE_LEVEL_DASHBOARD)

  useEffect(() => {
    if (
      activeCase &&
      activeCase.matter &&
      !loadingById &&
      !dataById &&
      !errorById
    ) {
      getNotificationsByCase({
        variables: { caseId: activeCase.matter.id }
      })
    } else if (
      activeCase &&
      !activeCase.isActiveCase &&
      !loading &&
      !data &&
      !error
    ) {
      getAllNotifications()
    }

    if (data) {
      setNotifications(data.notifications.get)
    } else if (dataById) {
      setNotifications(dataById.notifications.get)
    }
  }, [loading, data, error, loadingById, dataById, errorById, activeCase])

  useEffect(() => {
    if (markReadLoading) return

    if (activeCase && activeCase.matter) {
      getNotificationsByCase({
        variables: { caseId: activeCase.matter.id }
      })
    } else if (activeCase) {
      getAllNotifications({
        variables: { read: false }
      })
    }
  }, [markReadLoading])

  function getQuestionnaireShownInstructionsFlag(
    newActiveCase: ActiveCase
  ): boolean {
    if (newActiveCase) {
      if (newActiveCase.matter) {
        return newActiveCase.matter.questionnaireShownInstructionsFlag
      }
    }
    return false
  }

  function getQuestionnaireShownTermsAndConditionsFlag(
    newActiveCase: ActiveCase
  ): boolean {
    if (newActiveCase) {
      if (newActiveCase.matter) {
        return newActiveCase.matter.questionnaireShownTermsAndConditionsFlag
      }
    }
    return false
  }

  // triggered by handleNotificationClick
  useEffect(() => {
    if (!loadingCaseById && dataCaseById) {
      const newActiveCase = loadFirstActiveCaseRecordFromResponse(dataCaseById)
      dispatch(setActiveCase(newActiveCase))
      if (notification) {
        if (notification.type.toLocaleLowerCase() == 'document') {
          if (notification.description) {
            if (notification.description.includes('review')) {
              dispatch(setTabReview())
            } else {
              dispatch(setTabSubmit())
            }
          }
          history.push({
            pathname: '/dashboard/documents',
            state: {
              caseId: notification.caseId
            }
          })
        } else if (notification.type.toLocaleLowerCase() == 'course') {
          if (notification.description) {
            if (notification.description.includes('second')) {
              dispatch(setTabSecond())
            } else {
              dispatch(setTabFirst())
            }
          }
          history.push({
            pathname: '/dashboard/courses'
          })
        } else if (notification.type.toLocaleLowerCase() == 'questionnaire') {
          const questionnaireRoute = routeToQuestionnairePage(
            getQuestionnaireShownInstructionsFlag(newActiveCase),
            getQuestionnaireShownTermsAndConditionsFlag(newActiveCase)
          )
          history.push(questionnaireRoute)
        }
      }
    }

    if (errorCaseById) {
      console.log(errorCaseById)
    }
  }, [loadingCaseById, dataCaseById, errorCaseById, notification])

  if (error || errorById) {
    return <div>Error ...</div>
  }

  function handleNotificationClick(notification: Notification) {
    console.log(notification)
    setNotification(notification)
    getCaseDashboard({ variables: { caseId: notification.caseId } })

    notificationMarkAsRead({
      variables: {
        id: notification.id
      }
    })
  }

  const isPrevPageClientDash = history.location.state.prevPage === '/client'

  return (
    <>
      {(loading || loadingById) && loadingSpinnerPortal()}
      <GoBackHeader text='Notifications' />
      <NotificationsListContainer>
        {notifications.map((notification) => {
          const {
            case: notificationCase,
            id,
            type,
            description,
            notificationTime,
            read
          } = notification

          const style = !read ? notReadStyle : {}

          return (
            <NotificationsListItem
              key={id}
              role='list-item'
              style={style}
              onClick={() => handleNotificationClick(notification)}
            >
              <NotificationsListItemIcon>
                <span className={`mdi ${getIconByType(type)}`} />
              </NotificationsListItemIcon>
              <NotificationsListItemDescription>
                <span>
                  {type === 'Document'
                    ? truncateFileName(description)
                    : description}
                </span>
                {isPrevPageClientDash && (
                  <span>{notificationCase.caseType}</span>
                )}
              </NotificationsListItemDescription>
              <NotificationsListItemDate>
                <span>{getNotificationDate(notificationTime)}</span>
              </NotificationsListItemDate>
            </NotificationsListItem>
          )
        })}
      </NotificationsListContainer>
    </>
  )
}

export default NotificationsList
